import React from 'react';
import { Box } from '@mui/material';
import { theme, typography, colors, borderRadius, transitions, spacing, shadows } from '../../../themes/technicalAlerts';

/**
 * Base dropdown container with consistent styling
 */
export const DropdownContainer = ({ 
  isOpen,
  onClose,
  minWidth = 160,
  maxWidth = 320,
  maxHeight = 400,
  children,
  sx = {}
}) => {
  if (!isOpen) return null;

  return (
    <Box 
      sx={{
        position: 'absolute',
        top: '100%',
        left: '0',
        mt: spacing.xs,
        backgroundColor: colors.background.white,
        borderRadius: borderRadius.lg,
        boxShadow: shadows.popover,
        minWidth,
        maxWidth,
        maxHeight,
        zIndex: theme.zIndex.popover,
        ...sx
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </Box>
  );
};

/**
 * Simple list dropdown (for grouping strategies, etc.)
 */
export const SimpleListDropdown = ({
  isOpen,
  options = [],
  selectedValue,
  onSelect,
  getOptionLabel = (option) => option.label,
  getOptionValue = (option) => option.value,
  onClose
}) => {
  return (
    <DropdownContainer isOpen={isOpen} onClose={onClose}>
      {options.map((option) => {
        const value = getOptionValue(option);
        const label = getOptionLabel(option);
        const isSelected = selectedValue === value;
        
        return (
          <Box
            key={value}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(value);
              onClose?.();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: spacing.sm,
              py: spacing.xs,
              cursor: 'pointer',
              ...typography.body2,
              color: isSelected ? colors.primary : colors.text.primary,
              backgroundColor: isSelected ? colors.primaryBackground : 'transparent',
              transition: transitions.default,
              '&:hover': {
                backgroundColor: isSelected 
                  ? colors.primaryHover + '20'
                  : colors.background.hover
              }
            }}
          >
            {label}
            {isSelected && (
              <Box sx={{ ml: 'auto', color: colors.primary }}>✓</Box>
            )}
          </Box>
        );
      })}
    </DropdownContainer>
  );
};

/**
 * Shared dropdown control component for Technical Alerts system
 * Provides consistent dropdown button styling and behavior
 */
export const DropdownControl = ({
  children,
  isOpen = false,
  isActive = false,
  onClick,
  icon,
  label,
  value,
  activeCount,
  sx = {},
  ...props
}) => {
  const baseStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    px: spacing.sm,
    py: spacing.xs,
    cursor: 'pointer',
    borderRadius: borderRadius.md,
    backgroundColor: isActive 
      ? colors.primaryBackground 
      : colors.background.light,
    border: isActive
      ? `1px solid ${colors.primary}20` 
      : 'none',
    transition: transitions.default,
    position: 'relative',
    '&:hover': {
      backgroundColor: isActive
        ? colors.primaryHover + '20'
        : colors.background.hover
    },
    ...sx
  };

  const textStyles = {
    ...typography.body2,
    color: isActive
      ? colors.primaryHover
      : colors.text.subtle,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs
  };

  return (
    <Box onClick={onClick} sx={baseStyles} {...props}>
      <Box sx={textStyles}>
        {icon}
        {label}
        {value && (
          <>
            <Box sx={{ color: colors.text.hint }}>:</Box>
            <Box sx={{ color: colors.text.primary, fontWeight: 600 }}>
              {value}
            </Box>
          </>
        )}
        {activeCount > 0 && (
          <Box sx={{
            minWidth: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: colors.primary,
            color: colors.background.white,
            ...typography.micro,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ml: spacing.xs
          }}>
            {activeCount}
          </Box>
        )}
      </Box>
      
      {/* Dropdown arrow */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: transitions.default
      }}>
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
          <path 
            d="M1 1.5L6 6.5L11 1.5" 
            stroke={colors.text.hint} 
            strokeWidth="1.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </Box>
      
      {children}
    </Box>
  );
};

/**
 * Simplified unified dropdown component for non-filter use cases
 * Supports simple list and custom content types only
 */
export const UnifiedDropdown = ({
  // Button props
  icon,
  label,
  value,
  activeCount,
  isOpen = false,
  isActive = false,
  onClick,
  sx = {},
  
  // Dropdown content props
  type = 'simple', // 'simple' or 'custom'
  
  // Simple list props
  options = [],
  selectedValue,
  onSelect,
  getOptionLabel,
  getOptionValue,
  
  // Custom content
  children,
  
  // Common
  onClose,
  
  ...props
}) => {
  const renderDropdownContent = () => {
    switch (type) {
      case 'simple':
        return (
          <SimpleListDropdown
            isOpen={isOpen}
            options={options}
            selectedValue={selectedValue}
            onSelect={onSelect}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            onClose={onClose}
          />
        );
      
      case 'custom':
        return (
          <DropdownContainer isOpen={isOpen} onClose={onClose}>
            {children}
          </DropdownContainer>
        );
      
      default:
        return null;
    }
  };

  return (
    <DropdownControl
      isOpen={isOpen}
      isActive={isActive}
      onClick={onClick}
      icon={icon}
      label={label}
      value={value}
      activeCount={activeCount}
      sx={sx}
      {...props}
    >
      {renderDropdownContent()}
    </DropdownControl>
  );
};